import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { action, observable, toJS, computed, makeObservable, makeAutoObservable } from 'mobx';
// eslint-disable-next-line no-restricted-imports
import { apiCommon } from 'src/api/ApiCommon';
import { AutoWithdrawalHistoryResponseLKType } from 'src/api/config/automaticWithdrawal/getWithdrawalHistoryLK';
import { MobxMap } from 'src_common/common/mobx-utils/MobxMap';
import { WithdrawalViewModelType } from 'src/api/config/withdrawal/withdrawalList';
import { DeleteParamsWithdrawalRuleType } from 'src/api/config/automaticWithdrawal/deleteWithdrawalRule';
import { PostWithdrawalReviewType } from 'src/api/config/automaticWithdrawal/postWithdrawalReview';
import { WithdrawalRuleModelTypeLKType } from 'src/api/config/automaticWithdrawal/getWithdrawalRulesLK';
import { SdkStaff, NetDepositResponseType } from './sdk/staff';
import { PaymentProvidersType } from './ConfigState/types';
import { TrpcClient } from './TrpcClient';
import { PostPutParamsWithdrawalRuleLKType } from 'src/api/config/automaticWithdrawal/postPutWithdrawalRuleLK';

const convertToId = (item: WithdrawalModelType | WithdrawalRuleType): number => item.id;

export type RuleType = 'approve' | 'reject';

export const isForReviewTab = (reviewDate: string): boolean => {
    const doNotShowUntil = new Date(reviewDate).getTime();
    const dateNow = new Date().getTime();

    return doNotShowUntil > dateNow;
};

class WithdrawalItem {
    @observable private data: WithdrawalModelType | null;

    public constructor() {
        this.data = null;
        makeObservable(this);
    }

    public setData(data: WithdrawalModelType): void {
        this.data = data;
    }

    public getData(): WithdrawalModelType | null {
        return this.data;
    }

    public static create(): WithdrawalItem {
        return new WithdrawalItem();
    }
}

class RuleItem {
    @observable private data: WithdrawalRuleType | null;

    public constructor() {
        this.data = null;
        makeObservable(this);
    }

    public setData(data: WithdrawalRuleType): void {
        this.data = data;
    }

    public getData(): WithdrawalRuleType | null {
        return this.data;
    }

    public static create(): RuleItem {
        return new RuleItem();
    }
}
type WithdrawalModelType = WithdrawalViewModelType;
type WithdrawalRuleType = /*WithdrawalRuleModelType |*/ WithdrawalRuleModelTypeLKType;

export class AutomaticWithdrawalState {
    @observable public searchResultPending?: Array<number>;
    @observable public searchResultProcessedApprove?: Array<number>;
    @observable public searchResultProcessedReject?: Array<number>;
    @observable public searchResultProcessedReview?: Array<number>;
    @observable public searchAccountIdProcessed?: string;

    @observable public selectedTab: 'pending' | 'processed';
    @observable public searchValue: string;

    private readonly data: MobxMapAutoNew<number, WithdrawalItem>;
    private readonly ruleApproveData: MobxMapAutoNew<number, RuleItem>;
    private readonly ruleRejectData: MobxMapAutoNew<number, RuleItem>;
    private readonly list: Resource<Array<number>>;
    public readonly history: MobxMap<number, AutoWithdrawalHistoryResponseLKType | null>;
    private readonly listApprove: Resource<Array<number>>;
    private readonly listReject: Resource<Array<number>>;
    private readonly getAutomaticWithdrawal: Resource<boolean>;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly trpcClient: TrpcClient,
        private readonly paymentProviders: PaymentProvidersType
    ) {
        this.data = new MobxMapAutoNew(WithdrawalItem.create);
        this.ruleApproveData = new MobxMapAutoNew(RuleItem.create);
        this.ruleRejectData = new MobxMapAutoNew(RuleItem.create);
        this.selectedTab = 'pending';
        this.searchValue = '';

        this.getAutomaticWithdrawal = this.getAutomaticWithdrawal = new Resource(async (): Promise<boolean> => {
            if (this.paymentProviders.type === 'paysafe') {
                const response =
                    await this.trpcClient.client.automaticWithdrawals.getAutomaticWithdrawalFlagPaysafe.query();

                if (response.responseStatus === 'error') {
                    console.error(response.data.errors?.[0]?.message);
                    return false;
                }

                return response.data.data.automaticWithdrawalEnabled;
            }

            const response = await apiCommon.getAutomaticWithdrawal.run({});
            return response.automaticWithdrawalEnabled;
        });

        this.history = new MobxMap(async (userId: number): Promise<AutoWithdrawalHistoryResponseLKType | null> => {
            return (await apiCommon.autoWithdrawalHistory.run({ userId })).map((item) => {
                return {
                    ...item,
                    amount: item.amount?.toString(),
                };
            });
        });

        this.list = new Resource(async (): Promise<Array<number>> => {
            const data = (await apiCommon.withdrawalList.run({})).map((item) => {
                return {
                    ...item,
                    amount: parseFloat(item.amount.toString()),
                };
            });

            data.sort((a, b) => {
                if (a.tdate > b.tdate) {
                    return 1;
                }
                if (a.tdate < b.tdate) {
                    return -1;
                }
                return 0;
            });

            this.saveList(data);

            return data.map(convertToId);
        });

        this.listApprove = new Resource(async (): Promise<Array<number>> => {
            const data = (await apiCommon.getWithdrawalRules.run({ ruleType: 'approve' })).map((item) => {
                return {
                    ...item,
                    amount: item.amount?.toString(),
                };
            });

            data.sort((a, b) => {
                if (a.id < b.id) {
                    return 1;
                }
                if (a.id > b.id) {
                    return -1;
                }
                return 0;
            });

            this.saveRules(data, 'approve');

            return data.map(convertToId);
        });

        this.listReject = new Resource(async (): Promise<Array<number>> => {
            const data = (await apiCommon.getWithdrawalRules.run({ ruleType: 'reject' })).map((item) => {
                return {
                    ...item,
                    amount: item.amount?.toString(),
                };
            });

            data.sort((a, b) => {
                if (a.id < b.id) {
                    return 1;
                }
                if (a.id > b.id) {
                    return -1;
                }
                return 0;
            });

            this.saveRules(data, 'reject');

            return data.map(convertToId);
        });

        makeAutoObservable(this);
    }

    public selectFilterPending = (): void => {
        this.selectedTab = 'pending';
    };

    public selectFilterProcessed = (): void => {
        this.selectedTab = 'processed';
    };

    @action public searchByAccountNumber = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.searchValue = event.target.value;
        this.searchByAccountNumberPending(event.target.value);
        this.getSearchValueProcessed(event.target.value);
    };

    @computed public get numberOfItems(): number {
        return this.selectedTab === 'pending' ? this.numberOfPending : this.numberOfProcessed;
    }

    @computed private get listWithoutRejected(): Array<number> | null {
        if (this.allListApprove === null || this.allListReject === null || this.allList === null) {
            return null;
        }
        const ruleList: Array<string> = [];
        for (const item of this.allListReject) {
            const userData = this.ruleRejectData.get(item).getData();
            if (userData !== null) {
                ruleList.push(`${userData.accountId}-${userData.card}`);
            }
        }
        for (const item of this.allListApprove) {
            const userData = this.ruleApproveData.get(item).getData();
            if (userData !== null) {
                ruleList.push(`${userData.accountId}-${userData.card}`);
            }
        }
        const itemsForView = this.allList.filter((pendingItem) => {
            const withdrawalItem = this.getItem(pendingItem);

            if (withdrawalItem === null) {
                return false;
            }

            return ruleList.includes(`${withdrawalItem.accountId}-${withdrawalItem.cardNumber}`) === false;
        });

        return itemsForView;
    }

    @computed public get reviewList(): Array<number> | null {
        if (this.listWithoutRejected === null) {
            return null;
        }

        const itemsForView = this.listWithoutRejected.filter((item) => {
            const withdrawalModel = this.getItem(item);
            const reviewDate = withdrawalModel?.automaticWithdrawal?.doNotShowUntil;
            if (reviewDate !== undefined) {
                return isForReviewTab(reviewDate);
            }
            return false;
        });

        return itemsForView;
    }

    @computed public get pendingList(): Array<number> | null {
        const reviewList = this.reviewList;
        if (this.listWithoutRejected === null || reviewList === null) {
            return null;
        }

        return this.listWithoutRejected.filter((val) => !reviewList.includes(val));
    }

    @computed public get withdrawalIdList(): Array<number> | null {
        if (this.searchValue !== '') {
            return this.searchResultPending ?? null;
        }

        return this.pendingList;
    }
    @computed public get processedReviewIdList(): Array<number> | null {
        if (this.searchValue !== '') {
            return this.searchResultProcessedReview ?? null;
        }

        return this.reviewList;
    }

    @action private saveList(list: Array<WithdrawalModelType>): void {
        for (const item of list) {
            this.data.get(item.id).setData(item);
        }
    }

    @action private saveRules(list: Array<WithdrawalRuleType>, type: RuleType): void {
        if (type === 'approve') {
            for (const item of list) {
                this.ruleApproveData.get(item.id).setData(item);
            }
            return;
        }

        for (const item of list) {
            this.ruleRejectData.get(item.id).setData(item);
        }
    }

    public get allList(): Array<number> | null {
        const result = this.list.get();

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    public get allListApprove(): Array<number> | null {
        const result = this.listApprove.get();

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    public get allListReject(): Array<number> | null {
        const result = this.listReject.get();

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    public getItem(id: number): WithdrawalModelType | null {
        const item = this.data.get(id).getData();
        if (item === null) {
            return null;
        }

        return item;
    }

    public getItemRule(id: number, type: RuleType): WithdrawalRuleType | null {
        if (type === 'approve') {
            return this.ruleApproveData.get(id).getData();
        }

        return this.ruleRejectData.get(id).getData();
    }

    @computed public get automaticWithdrawalSwitchValue(): boolean | null {
        const result = this.getAutomaticWithdrawal.get();

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    @computed public get numberOfProcessed(): number {
        const { allListApprove, allListReject, reviewList } = this;

        const lenghtApprove = allListApprove === null ? 0 : allListApprove.length;
        const lenghtReject = allListReject === null ? 0 : allListReject.length;
        const lenghtReview = reviewList === null ? 0 : reviewList.length;
        return lenghtApprove + lenghtReject + lenghtReview;
    }

    @computed public get numberOfPending(): number {
        return this.pendingList === null ? 0 : this.pendingList.length;
    }

    @action public searchByAccountNumberPending = (accountId: string): void => {
        const reviewListFiltered = this.reviewList?.filter((pendingItemId) => {
            const item = this.data.get(pendingItemId).getData();
            return item?.accountId.toString().includes(accountId);
        });

        const pendingListFiltered = this.pendingList?.filter((pendingItemId) => {
            const item = this.data.get(pendingItemId).getData();
            return item?.accountId.toString().includes(accountId);
        });
        this.searchResultProcessedReview = reviewListFiltered;
        this.searchResultPending = pendingListFiltered;
    };

    @action public getSearchValueProcessed = (accountId: string): void => {
        this.searchByAccountNumberProcessed(accountId);
    };

    @action private searchByAccountNumberProcessed = (accountId: string): void => {
        this.searchResultProcessedApprove = toJS(
            this.searchRule(accountId, this.allListApproveArray, this.ruleApproveData)
        );
        this.searchResultProcessedReject = toJS(
            this.searchRule(accountId, this.allListRejectArray, this.ruleRejectData)
        );
    };

    private searchRule(
        accountId: string,
        listArray: Array<number>,
        ruleData: MobxMapAutoNew<number, RuleItem>
    ): Array<number> {
        return listArray.filter((processedItemId) => {
            const item = ruleData.get(processedItemId).getData();
            return item?.accountId.toString().includes(accountId);
        });
    }

    @computed private get allListApproveArray(): Array<number> {
        return this.allListApprove ?? [];
    }

    @computed private get allListRejectArray(): Array<number> {
        return this.allListReject ?? [];
    }

    public async execSetOrUpdateRule(body: PostPutParamsWithdrawalRuleLKType): Promise<void> {
        const bodyOld = {
            ...body,
            amount: body.amount === null ? null : parseFloat(body.amount),
        };

        switch (this.paymentProviders.type) {
            case 'paysafe':
                if (bodyOld.id === undefined || bodyOld.id === null) {
                    await this.trpcClient.client.automaticWithdrawals.createAutomaticWithdrawalRulePaysafe.mutate({
                        card: bodyOld.card,
                        accountId: bodyOld.accountId,
                        ruleType: bodyOld.ruleType,
                        amount: bodyOld.amount,
                        applicationName: 'banking-interface',
                        note: bodyOld.note,
                    });
                    break;
                }

                await this.trpcClient.client.automaticWithdrawals.updateAutomaticWithdrawalRulePaysafe.mutate({
                    card: bodyOld.card,
                    accountId: bodyOld.accountId,
                    ruleType: bodyOld.ruleType,
                    amount: bodyOld.amount,
                    applicationName: 'banking-interface',
                    note: bodyOld.note,
                    id: bodyOld.id,
                });
                break;
            default:
                await apiCommon.postPutWithdrawalRule.run(bodyOld);
        }

        await this.refresh();
        if (this.searchAccountIdProcessed !== undefined) {
            this.searchByAccountNumberProcessed(this.searchAccountIdProcessed);
        }
    }

    public async execDelete(body: DeleteParamsWithdrawalRuleType): Promise<void> {
        if (this.paymentProviders.type === 'paysafe') {
            await this.trpcClient.client.automaticWithdrawals.deleteWithdrawalRulePaysafe.mutate({
                customerId: body.accountId,
                ruleId: body.id,
            });
        } else {
            await apiCommon.deleteWithdrawalRule.run(body);
        }

        await this.refresh();
        if (this.searchAccountIdProcessed !== undefined) {
            this.searchByAccountNumberProcessed(this.searchAccountIdProcessed);
        }
    }

    public async execReview(body: PostWithdrawalReviewType): Promise<void> {
        await apiCommon.postWithdrawalReview.run(body);
        await this.refresh();
    }

    @action public async refresh(): Promise<void> {
        await this.list.refresh();
        await this.listApprove.refresh();
        await this.listReject.refresh();
    }

    @action public async onSwitchAutomaticWithdrawal(toggleValue: boolean): Promise<void> {
        if (this.paymentProviders.type === 'paysafe') {
            await this.trpcClient.client.automaticWithdrawals.setAutomaticWithdrawalFlagPaysafe.mutate({
                automaticWithdrawalEnabled: toggleValue,
            });
        } else {
            await apiCommon.putAutomaticWithdrawal.run({ automaticWithdrawalEnabled: toggleValue });
        }
        await this.getAutomaticWithdrawal.refresh();
    }

    @action public async getNetDeposit(userId: number): Promise<NetDepositResponseType> {
        return await this.sdkStaff.postNetDeposit({ customer_id: userId, requestBody: { page: 1, perPage: 10000 } });
    }

    @action public async getHistory(userId: number): Promise<AutoWithdrawalHistoryResponseLKType> {
        return (await apiCommon.autoWithdrawalHistory.run({ userId })).map((item) => {
            return {
                ...item,
                amount: item.amount?.toString(),
            };
        });
    }

    @action public onRefreshList = async (): Promise<void> => {
        await this.list.refresh();
    };
}
